import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import {
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Breadcrumb,
  Button,
} from 'react-bootstrap';
import {
  fetchSharePointDocuments,
  selectSharePointDocuments,
  selectSharePointLoading,
  selectSharePointError,
  selectSharePointBreadcrumb,
  setBreadcrumb,
  updateCurrentFolder,
} from '../../redux/slices/sharePointSlice';

function SharePointDocumentBrowser() {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();

  // Environment variables
  const siteId = process.env.REACT_APP_MANUFACTUREORDERS_SITE_ID;
  const driveId = process.env.REACT_APP_MANUFACTUREORDERS_DRIVE_ID;

  // Redux state
  const documents = useSelector(selectSharePointDocuments);
  const loading = useSelector(selectSharePointLoading);
  const error = useSelector(selectSharePointError);
  const breadcrumb = useSelector(selectSharePointBreadcrumb);

  // Local state
  const [accessToken, setAccessToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  // Get access token on component mount
  useEffect(() => {
    const getAccessToken = async () => {
      try {
        if (!instance || !accounts || accounts.length === 0) {
          setTokenError('No MSAL instance or accounts available');
          return;
        }

        const tokenRequest = {
          scopes: ['https://graph.microsoft.com/.default'],
          account: accounts[0],
        };

        const response = await instance.acquireTokenSilent(tokenRequest);
        setAccessToken(response.accessToken);
        setTokenError(null);
      } catch (error) {
        console.error('Failed to acquire token:', error);
        setTokenError('Failed to acquire access token. Please sign in.');
      }
    };

    if (instance && accounts.length > 0) {
      getAccessToken();
    }
  }, [instance, accounts]);

  // Fetch documents on mount or when access token changes
  useEffect(() => {
    if (siteId && driveId && accessToken && !tokenError) {
      dispatch(
        fetchSharePointDocuments({
          siteId,
          driveId,
          folderId: 'root',
          accessToken,
        })
      );
    }
  }, [accessToken, siteId, driveId, dispatch, tokenError]);

  const handleFolderClick = (item) => {
    if (item.folder) {
      // Update breadcrumb
      const newBreadcrumb = [...breadcrumb, { name: item.name, folderId: item.id }];
      dispatch(setBreadcrumb(newBreadcrumb));
      dispatch(updateCurrentFolder(item.id));

      // Fetch items from the new folder
      dispatch(
        fetchSharePointDocuments({
          siteId,
          driveId,
          folderId: item.id,
          accessToken,
        })
      );
    }
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    const folderId = newBreadcrumb[newBreadcrumb.length - 1].folderId;

    dispatch(setBreadcrumb(newBreadcrumb));
    dispatch(updateCurrentFolder(folderId));

    // Fetch items from the breadcrumb folder
    dispatch(
      fetchSharePointDocuments({
        siteId,
        driveId,
        folderId,
        accessToken,
      })
    );
  };

  const handleViewClick = (item) => {
    if (item['@microsoft.graph.downloadUrl']) {
      const fileExtension = item.name.split('.').pop().toLowerCase();
      
      // For PDFs, use the webUrl if available, otherwise use download URL
      let viewUrl = item['@microsoft.graph.downloadUrl'];
      
      if (fileExtension === 'pdf') {
        // Try to use webUrl first (allows inline viewing)
        if (item.webUrl) {
          viewUrl = item.webUrl;
        }
      }
      
      // Open in new tab
      window.open(viewUrl, '_blank');
    }
  };

  const isViewableFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop().toLowerCase();
    return ['pdf'].includes(extension);
  };

  const handleDownloadClick = (item) => {
    if (item['@microsoft.graph.downloadUrl']) {
      window.open(item['@microsoft.graph.downloadUrl'], '_blank');
    }
  };

  if (tokenError) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{tokenError}</Alert>
      </Container>
    );
  }

  if (!siteId || !driveId) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          SharePoint configuration is missing. Please check environment variables.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Manufacture Orders Documents</h2>
        </Col>
      </Row>

      {/* Breadcrumb Navigation */}
      {breadcrumb.length > 0 && (
        <Row className="mb-3">
          <Col>
            <Breadcrumb>
              {breadcrumb.map((item, index) => (
                <Breadcrumb.Item
                  key={index}
                  onClick={() => handleBreadcrumbClick(index)}
                  style={{ cursor: 'pointer' }}
                  active={index === breadcrumb.length - 1}
                >
                  {item.name}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </Col>
        </Row>
      )}

      {/* Error Alert */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible>
              <strong>Error:</strong> {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Loading State */}
      {loading && (
        <Row className="mb-3">
          <Col className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Col>
        </Row>
      )}

      {/* Documents Table */}
      {!loading && documents.length > 0 && (
        <Row className="mb-3">
          <Col>
            <Table striped bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '45%' }}>Name</th>
                  <th style={{ width: '15%' }}>Type</th>
                  <th style={{ width: '15%' }}>Modified</th>
                  <th style={{ width: '10%' }}>Size</th>
                  <th style={{ width: '15%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.folder ? (
                        <div
                          onClick={() => handleFolderClick(item)}
                          style={{ cursor: 'pointer', color: '#0066cc', textDecoration: 'underline' }}
                        >
                          📁 {item.name}
                        </div>
                      ) : (
                        <div>📄 {item.name}</div>
                      )}
                    </td>
                    <td>{item.folder ? 'Folder' : item.file?.mimeType || 'File'}</td>
                    <td>
                      {item.lastModifiedDateTime
                        ? new Date(item.lastModifiedDateTime).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      {item.size
                        ? `${(item.size / 1024).toFixed(2)} KB`
                        : '-'}
                    </td>
                    <td>
                      {!item.folder && item['@microsoft.graph.downloadUrl'] && (
                        <div className="d-flex gap-2">
                          {isViewableFile(item.name) && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewClick(item)}
                            >
                              👁️ View
                            </Button>
                          )}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleDownloadClick(item)}
                          >
                            ⬇️ Download
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}

      {/* No Documents Message */}
      {!loading && documents.length === 0 && !error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="info">No documents found in this location.</Alert>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default SharePointDocumentBrowser;
